import { ChevronRightRounded } from '@mui/icons-material'
import { Box, Card, CardContent, CardHeader, SxProps, Typography } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getAdoptionAmountDisplay,
  getColorForAnimalAdoptionStatus,
  getColorForAnimalAdoptionStep,
  getReadableAnimalAdoptionOfferType,
  getReadableAnimalAdoptionStatus,
  getReadableAnimalAdoptionStep,
  getReadableCannotBeAdoptedStatus,
} from '../../../../domain/Animal/AnimalDisplay'
import {
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoStatus,
  AnimalDto,
} from '../../../../interactors/gen/backendClient'
import { useCanEditAnimal } from '../../../../store/useCanEditAnimal'
import { useAdoptersStore } from '../../../../store/AdoptersStore'
import { useFetchOrRefreshAdoptersOnMount } from '../../../../store/useFetchOrRefreshAdoptersOnMount'
import { toDayJSFromAll } from '../../../../utils/date/DateOnly'
import { ReadOnlyChip } from '../../../common/ReadOnlyChip'

interface Props {
  animal: AnimalDto
  sx?: SxProps
}

const EMPTY_VALUE = '...'

const formatDateOrEmpty = (value?: string) => {
  if (!value) return EMPTY_VALUE
  const parsedDate = toDayJSFromAll(value)
  if (!parsedDate || !parsedDate.isValid()) return EMPTY_VALUE
  return parsedDate.format('DD/MM/YYYY')
}

type GroupItem = {
  key: string
  label?: string
  value?: string
  chipColor?: string
  isStatusChip?: boolean
}

const SummaryItem: React.FC<{ label: string; value: string; chipColor?: string }> = ({ label, value, chipColor }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: 0.2,
      minWidth: 0,
      whiteSpace: 'nowrap',
      width: 'fit-content',
      maxWidth: '100%',
    }}
  >
    {label ? (
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 600,
          color: '#7A8197',
          flexShrink: 0,
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
    ) : null}
    {chipColor ? (
      <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
        <ReadOnlyChip label="" value={value || EMPTY_VALUE} chipColor={chipColor} />
      </Box>
    ) : (
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 500,
          color: '#2B4166',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          lineHeight: 1.2,
        }}
        title={value}
      >
        {value || EMPTY_VALUE}
      </Typography>
    )}
  </Box>
)

export const AnimalResumeAdoptionCard: React.FC<Props> = ({ animal, sx }) => {
  const navigate = useNavigate()
  const canEditAnimal = useCanEditAnimal()

  useFetchOrRefreshAdoptersOnMount()

  const adoptersStore = useAdoptersStore()
  const adopter = adoptersStore.adopters.find((item) => item.id === animal.adoption.adopterId)

  const priceDisplay = animal.adoption.offerType
    ? getAdoptionAmountDisplay(animal.adoption.offerType, animal.adoption?.priceInEuros)
    : EMPTY_VALUE
  const offerTypeDisplay = animal.adoption.offerType
    ? getReadableAnimalAdoptionOfferType(animal.adoption.offerType)
    : EMPTY_VALUE
  const adopterDisplay = adopter ? `${adopter.firstName} ${adopter.lastName}`.trim() : EMPTY_VALUE
  const stepDisplay = animal.adoption.step ? getReadableAnimalAdoptionStep(animal.adoption.step) : EMPTY_VALUE
  const adoptionDateDisplay = formatDateOrEmpty(animal.adoption?.adoptionDate)
  const icadDateDisplay = formatDateOrEmpty(animal.adoption?.exitDate)
  const deathDateDisplay = formatDateOrEmpty(animal.adoption?.goodbyeDate)
  const statusChipValue =
    animal.adoption.status === AnimalAdoptionDtoStatus.CannotBeAdopted
      ? animal.adoption.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily
        ? 'FALD'
        : `NA - ${
            animal.adoption.cannotBeAdoptedStatus
              ? getReadableCannotBeAdoptedStatus(animal.adoption.cannotBeAdoptedStatus, { short: true })
              : EMPTY_VALUE
          }`
      : getReadableAnimalAdoptionStatus(animal.adoption.status)

  const groupedSummaryItems = (() => {
    const statusItem: GroupItem = {
      key: 'status',
      value: statusChipValue,
      chipColor: getColorForAnimalAdoptionStatus(animal.adoption.status),
      isStatusChip: true,
    }

    switch (animal.adoption.status) {
      case AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable:
        return {
          left: [statusItem, { key: 'price', label: 'Montant', value: priceDisplay }],
          right: [
            { key: 'adopter', label: 'Adoptant', value: adopterDisplay },
            {
              key: 'step',
              value: stepDisplay,
              chipColor: animal.adoption.step ? getColorForAnimalAdoptionStep(animal.adoption.step) : undefined,
            },
          ],
        }
      case AnimalAdoptionDtoStatus.HasBeenAdopted:
        return {
          left: [statusItem, { key: 'adopter', label: 'Adoptant', value: adopterDisplay }],
          right: [
            { key: 'physicalExit', label: 'Sortie physique', value: adoptionDateDisplay },
            { key: 'icadExit', label: 'Sortie ICAD', value: icadDateDisplay },
          ],
        }
      case AnimalAdoptionDtoStatus.Adoptable:
        return {
          left: [statusItem],
          right: [
            { key: 'price', label: 'Montant', value: priceDisplay },
            { key: 'offerType', label: "Type d'offre", value: offerTypeDisplay },
          ],
        }
      case AnimalAdoptionDtoStatus.CannotBeAdopted:
        if (animal.adoption.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.Dead) {
          return {
            left: [statusItem],
            right: [{ key: 'deathDate', label: 'Date de décès', value: deathDateDisplay }],
          }
        }
        if (
          animal.adoption.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation
        ) {
          return {
            left: [statusItem],
            right: [{ key: 'transferDate', label: 'Date du transfert', value: icadDateDisplay }],
          }
        }
        if (animal.adoption.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.Released) {
          return {
            left: [statusItem],
            right: [
              { key: 'releaseDate', label: 'Date de sortie', value: adoptionDateDisplay },
              { key: 'location', label: 'Lieu de stationnement', value: animal.adoption.goodbyeDetails || EMPTY_VALUE },
            ],
          }
        }
        if (animal.adoption.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.SocialSecurity) {
          return {
            left: [statusItem],
            right: [
              {
                key: 'socialSecurityEntry',
                label: 'Garde sociale — entrée',
                value: formatDateOrEmpty(animal.adoption?.socialSecurityEntryDate),
              },
              {
                key: 'socialSecurityExit',
                label: 'Garde sociale — sortie',
                value: formatDateOrEmpty(animal.adoption?.socialSecurityExitDate),
              },
            ],
          }
        }
        if (animal.adoption.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.Lost) {
          return {
            left: [statusItem],
            right: [{ key: 'lossDate', label: 'Date de perte', value: deathDateDisplay }],
          }
        }
        if (animal.adoption.cannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily) {
          return {
            left: [statusItem],
            right: [
              { key: 'physicalExit', label: 'Sortie physique', value: adoptionDateDisplay },
              { key: 'icadExit', label: 'Sortie ICAD', value: icadDateDisplay },
            ],
          }
        }
        return {
          left: [statusItem],
          right: [
            {
              key: 'reason',
              label: 'Motif',
              value: animal.adoption.cannotBeAdoptedStatus
                ? getReadableCannotBeAdoptedStatus(animal.adoption.cannotBeAdoptedStatus, { short: true })
                : EMPTY_VALUE,
            },
          ],
        }
      default:
        return { left: [statusItem], right: [] }
    }
  })()

  const renderGroupItem = (item: GroupItem) => {
    if (item.isStatusChip) {
      return <ReadOnlyChip label="" value={item.value || EMPTY_VALUE} chipColor={item.chipColor} emphasis />
    }

    return <SummaryItem label={item.label || ''} value={item.value || EMPTY_VALUE} chipColor={item.chipColor} />
  }

  return (
    <Card sx={sx}>
      <CardHeader
        title={`Suivi d'adoption`}
        titleTypographyProps={{ sx: { fontSize: { xs: 15.5, sm: 16 }, fontWeight: 700, lineHeight: 1.2 } }}
        action={
          canEditAnimal && (
            <Box
              onClick={() => navigate('./?tab=adoption')}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: '#203864',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#203864',
                  backgroundColor: 'rgba(32, 56, 100, 0.08)',
                }}
              >
                <ChevronRightRounded sx={{ fontSize: 20, color: 'inherit' }} />
              </Box>
            </Box>
          )
        }
        sx={{
          pb: 0.5,
          '& .MuiCardHeader-action': {
            mr: 0,
            alignSelf: 'flex-start',
          },
        }}
      />
      <CardContent sx={{ pt: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: { xs: 1.15, md: 2.5 },
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(groupedSummaryItems.left.length, 1)}, minmax(0, 1fr))`,
              alignItems: 'center',
              gap: { xs: 1, md: 1.5 },
              minWidth: 0,
              flex: '1 1 340px',
              maxWidth: '100%',
            }}
          >
            {groupedSummaryItems.left.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minWidth: 0,
                }}
              >
                {renderGroupItem(item)}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(groupedSummaryItems.right.length, 1)}, minmax(0, 1fr))`,
              alignItems: 'center',
              gap: { xs: 1, md: 1.5 },
              minWidth: 0,
              flex: '1 1 340px',
              maxWidth: '100%',
            }}
          >
            {groupedSummaryItems.right.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  minWidth: 0,
                }}
              >
                {renderGroupItem(item)}
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
